import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useProjects } from 'context/project-context';
import useAxios from 'axios-hooks';
import { getAuthHeader } from 'utils/auth';
import { PageAlert, ErrorBoundary, ContentContainer } from 'components';
import DashboardTop from 'views/Dashboard/components/DashboardTop';
import DashboardWidgets, { SeverityAndFiltersWidget } from 'views/Dashboard/components/DashboardWidgets';
import { MainChart } from 'views/Dashboard/components/Charts';
import styled from '@emotion/styled';
import { colors, elements } from 'style/theme';
import { SCORES } from 'components/Score';
import NoDataChart from './components/Charts/NoDataChart';
import { getDefaultDate, getDashboardFromLocalstorage } from './utils';
import { notificationToast } from 'components/NotificationToast';
import { DASHBOARD_STORAGE_KEY } from 'utils/constants';
import { DashboardProvider, useUpdateChartLayoutCallbacks } from 'context/dashboard-context';

const StyledContainer = styled.div({
  padding: 10,
  background: colors.brandWhite,
  borderRadius: 5,
  border: elements.brandBorder,
  boxShadow: elements.brandBoxShadow,
  display: 'grid',
  gridTemplateColumns: '1fr 6fr'
});

const StyleLeftPane = styled.div`
  text-align: right;
  margin-right: 2rem;
`;
const StyledValue = styled.div`
  font-size: ${(props) => (props.isScore ? '4rem' : '2.2rem')};
  font-weight: 700;
  color: ${(props) => (props.isScore ? SCORES[props.value][0] : '#585858')};
`;
const StyledTitle = styled.div`
  font-size: 13px;
  color: #000;
  font-weight: 300;
  top: ${(props) => (props.isScore ? '-22px' : '-12px')};
  position: relative;
`;

function Dashboard() {
  const { project } = useProjects();
  const [filters, setFilters] = useState(null);

  const [
    { data: dashboardData = {}, loading: loadingDashboard, error: dashboardError },
    applyDashboardFilters
  ] = useAxios(
    {
      method: 'POST',
      url: `/${project.name}/dashboard/widgets`,
      headers: getAuthHeader()
    },
    { manual: true }
  );

  const [{ data: chartData, loading: chartLoading, error: chartError }, getChartData] = useAxios(
    {
      method: 'POST',
      url: `/${project.name}/dashboard/chart-data`,
      headers: getAuthHeader()
    },
    { manual: true }
  );

  const [{ data: scanSettingsList, error: scanSettingsListError }, getScanSettings] = useAxios(
    {
      method: 'POST',
      url: `/${project.name}/scan-settings/list`,
      headers: getAuthHeader()
    },
    { manual: true }
  );

  const getDefaultFilters = async () => {
    const defaultGroup = 'day';
    const check1stLaunchDate = '01-01-2020';

    const formatDate = (dateObject) =>
      dateObject.toLocaleDateString({ month: 'numeric', day: 'numeric', year: '2-digit' }).replaceAll('/', '-');

    const today = formatDate(new Date());

    /* logic to use first scan date as default start date
    const {
      data: {
        dates: [firstScanDate]
       }
     } = await getChartData({ data: { from: check1stLaunchDate, to: today, group: defaultGroup } });
  */

    return {
      // from: formatDate(new Date(firstScanDate)),
      from: check1stLaunchDate,
      to: today,
      group: defaultGroup
    };
  };

  const setDateRangeAndGroupFilters = async () => setFilters(getDashboardFromLocalstorage() || (await getDefaultFilters()));

  useEffect(() => {
    setDateRangeAndGroupFilters();
    getScanSettings({data:{}});
  }, []);

  useEffect(() => {
    if (filters) {
      localStorage.setItem(DASHBOARD_STORAGE_KEY, JSON.stringify(filters));
      applyDashboardFilters({ data: filters });
      getChartData({ data: filters });
    }
  }, [filters]);

  useEffect(() => {
    if (dashboardError) {
      notificationToast({
        message: 'Error',
        description: dashboardError?.response?.data?.Errors,
        icontype: 'alert'
      });
    }
  }, [dashboardError]);

  const formatErrors = (value) => {
    const k = 1000;
    const m = 1000000;
    const num = Number(value);

    if (num >= k && num < m) {
      return `${Math.round(num / k)}K`;
    } else if (num >= m) {
      return `${Math.round(num / m)}M`;
    }
    return value;
  };

  const error = dashboardError?.response?.data?.Errors;
  const chartErr = chartError?.response?.data?.Errors;
  const widgetsData = error ? {} : dashboardData;

  const { updateChartLayoutCallbacks } = useUpdateChartLayoutCallbacks();

  useLayoutEffect(() => {
    const invokeCallbacks = () => updateChartLayoutCallbacks.forEach((callback) => callback());
    window.addEventListener('resize', () => invokeCallbacks);
    return () => {
      window.removeEventListener('resize', invokeCallbacks);
    };
  }, []);

  return (
    <DashboardProvider>
      <ErrorBoundary>
        <DashboardTop />
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gridGap: 20 }}>
          <StyledContainer style={{ alignItems: 'center' }}>
            <StyleLeftPane>
              {widgetsData &&
                Object.entries(widgetsData).map(([key, value]) => {
                  const populateKeys = ['score', 'scans', 'pages', 'errors'];
                  const isScore = key === 'score';
                  const isError = key === 'errors';
                  value = isError ? formatErrors(value) : value;
                  return (
                    <>
                      {populateKeys.includes(key) ? (
                        <div style={{ marginBottom: 16 }}>
                          <StyledValue isScore={isScore} value={value}>
                            {value}
                          </StyledValue>
                          <StyledTitle isScore={isScore}>{key?.toUpperCase()}</StyledTitle>
                        </div>
                      ) : null}
                    </>
                  );
                })}
            </StyleLeftPane>
            <div style={{ marginRight: 30 }}>
              {chartData && !chartErr ? (
                <MainChart filters={filters} setFilters={setFilters} chartData={chartData} loading={chartLoading} />
              ) : (
                <NoDataChart />
              )}
            </div>
          </StyledContainer>
          <SeverityAndFiltersWidget
            scanSettingsList={scanSettingsList}
            filters={filters}
            setFilters={setFilters}
            data={widgetsData}
          />
        </div>
        <DashboardWidgets data={widgetsData} />
      </ErrorBoundary>
    </DashboardProvider>
  );
}

export default Dashboard;
